import { CalendarDays } from "lucide-react";

import DatePickerComponent from "@/design-system/day-picker";
import KarendaaDayPicker from "@/design-system/day-picker";
import { CalendarResources, type ICalendarResources } from "@/design-system/day-picker/calendar.Resources";
import { CalendarResourcesEn } from "@/design-system/day-picker/calendar.Resources.En";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts";
import { browserStatics } from "@/src/shared/BrowserStatic";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { IErrorComponent, IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType, clearError, displayError, validate } from "@Components/Modules/ErrorHandler";
import type { IAccountFormat, UserLanguageType } from "@Components/Modules/Helpers";
import { accountNumberFormat, formatDate } from "@Components/Modules/Helpers";
import { isNullOrEmpty } from "@Components/Modules/Validations";
import type { ISearchBox, ISearchBoxResultItem } from "@Components/SearchBox";
import { SearchBox } from "@Components/SearchBox";
import type { ITextbox } from "@Components/Textbox";
import { Textbox } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";
import type { IRadioButtonList } from "@Layout/RadioButtonList";
import { RadioButtonList } from "@Layout/RadioButtonList";
import * as stylex from "@stylexjs/stylex";

import type { IButtonsBar } from "../common/ButtonsBar";
import { ButtonsBar } from "../common/ButtonsBar";
import type { INavigationModalActions } from "../common/NavigationModal";
import type { IUserCommunicationTemplate } from "../common/UserCommunicationTemplate";
import { type IInputTracker, SetInputTrackerProps } from "../telemetry/InputTracker";
import type { ICashbackClaimFields } from "./CashbackCommonInterfaces";
import { CashbackClaimType, type VisaChannelType } from "./CashbackCommonInterfaces";
import {
    CashbackMissingClaimFormResources,
    type ICashbackMissingClaimFormResources,
} from "./CashbackMissingClaimForm.Resources";
import { CashbackMissingClaimFormResourcesEn } from "./CashbackMissingClaimForm.Resources.En";

import * as css from "./CashbackMissingClaimForm.module.css";

export interface ICashbackMissingClaimForm
    extends INavigationModalActions<ICashbackClaimFields>, IComponentAjax<IConfirmClaimDataResult> {
    title: string;
    language?: UserLanguageType;
    brandSearchLabel: string;
    brandSearch: IInputTracker<ISearchBox>;
    transactionDate: string;
    purchaseAmountLabel: string;
    purchaseAmountTextbox: IInputTracker<ITextbox>;
    orderIdLabel: string;
    orderIdTextbox: IInputTracker<ITextbox>;
    buttonsBar: IButtonsBar;
    orderIdInvalidError: IValidator;
    amountInvalidError: IValidator;
    brandInvalidError: IValidator;
    dateInvalidError: IValidator;
    dateRangeInvalidError: IValidator;
    visaChannelInvalidError: IValidator;
    dateRangeLessThan3Days: IValidator;
    resources: ICashbackMissingClaimFormResources;
    resourcesCalendar: ICalendarResources;
}

interface ICashbackMissingClaimFormState
    extends IComponentAjaxState, INavigationModalActions<ICashbackClaimFields> {
    purchaseAmount: string;
    purchaseAmountValue?: number;
    date?: Date;
    brandName: string;
    brandId?: string;
    orderId: string;
    brandInfo?: string;
    visaChannel?: VisaChannelType;
    imageUrl?: string;
    orderIdErrorMessage?: string;
    dateWarning?: boolean;
    orderIdWatermarkText?: string;
    orderIdText?: string;
}

interface IConfirmClaimDataResult {
    isValidated: boolean;
    errorMessage: string;
    communicationTemplate?: IUserCommunicationTemplate;
}

interface ISearchBoxResultItemExtension extends ISearchBoxResultItem {
    info?: string;
    logo?: string;
}

function subtractDays(days: number) {
    const today = new Date();
    const resultDate = new Date(today);
    resultDate.setDate(today.getDate() - days);
    return resultDate;
}

const dateCompare = (date: Date, extraDay: number) => {
    if (date) {
        const todayPlusExtraDay = subtractDays(extraDay);
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) >= todayPlusExtraDay;
    }
    return false;
};

class CashbackMissingClaimForm extends ComponentAjax<
    IConfirmClaimDataResult,
    ICashbackMissingClaimForm,
    ICashbackMissingClaimFormState
> {
    private get renderTransactionDate() {
        const validateDateChange = (newDate: Date) => {
            const dateWarning = dateCompare(newDate, 3);

            clearError(this, this.errors.dateRangeLessThan3Days);

            this.setState({ date: newDate, dateWarning }, () => {
                validate(this, this.errors.dateRangeLessThan3Days);
            });
        };

        return (
            <div className={css.datePicker}>
                <label className={css.standardModalLabel} htmlFor="transactionDate">
                    {this.props.resources.transactionDateLabel}
                </label>

                <div>
                    <DatePickerComponent
                        id="transactionDate"
                        minDate={subtractDays(32)}
                        maxDate={subtractDays(4)}
                        defaultValue={this.state.date}
                        monthNames={this.props.resourcesCalendar.months}
                        weekDays={this.props.resourcesCalendar.weekdays}
                        onChange={(newPate) => {
                            sendTelemetryEvent({
                                eventName: "cshbck-ClmMakeDateEdit",
                                actionType: "Undefined",
                            });

                            validateDateChange(newPate);
                        }}
                        inputPlaceHolder={this.props.resources.transactionDatePlaceholder}
                        data-testid="prof-pers-bdate-input"
                    >
                        <KarendaaDayPicker.Trigger asChild>
                            <span aria-label="Open date picker" {...stylex.props(styles.calendarButton)}>
                                <CalendarDays size={16} />
                            </span>
                        </KarendaaDayPicker.Trigger>
                    </DatePickerComponent>

                    <ErrorContainer {...this.errors.dateInvalidError.container} />
                    <ErrorContainer {...this.errors.dateRangeInvalidError.container} />
                    <ErrorContainer {...this.errors.dateRangeLessThan3Days.container} />
                </div>
            </div>
        );
    }

    private get renderBrand() {
        const searchTrackerProps: ISearchBox = SetInputTrackerProps({
            ...this.props.brandSearch,
            focusTelemetryCustom: {
                eventName: "cshbck-clmMakeBrandEdit",
                actionType: "Undefined",
            },
            properties: {
                ...this.props.brandSearch.properties,
                onSelect: this.onSelectBrand,
                onBlurSelectedItem: this.onBlurSelected,
                value: this.state.brandName,
                onChange: this.onChangeBrand,
            },
        });

        return (
            <div className={css.searchBrand}>
                <label className={css.standardModalLabel} htmlFor={`${searchTrackerProps.clientId}-input`}>
                    {this.props.brandSearchLabel}
                </label>
                <div>
                    <SearchBox {...searchTrackerProps} ref="brand-textbox" />
                    <ErrorContainer {...this.errors.brandInvalidError.container} />
                </div>
            </div>
        );
    }

    private get renderOrderId() {
        const orderIdTextbox = SetInputTrackerProps<ITextbox>({
            ...this.props.orderIdTextbox,
            focusTelemetryCustom: { eventName: "cshbck-clmMakeOrderIdEdit", actionType: "Undefined" },
            properties: {
                ...this.props.orderIdTextbox.properties,
                onChange: (event) =>
                    this.setState({ orderId: event.target.value, orderIdErrorMessage: undefined }),
                value: this.state.orderId,
                watermarkText:
                    this.state.orderIdWatermarkText || this.props.orderIdTextbox.properties.watermarkText,
            },
        });

        return (
            <div>
                <label className={css.standardModalLabel} htmlFor={`${orderIdTextbox.clientId}-input`}>
                    {this.state.orderIdText || this.props.orderIdLabel}
                </label>
                <div>
                    <Textbox {...orderIdTextbox} ref="orderId-textbox" />
                    <ErrorContainer {...this.errors.orderIdInvalidError.container} />
                </div>
            </div>
        );
    }

    private get renderVisaChannel() {
        const radioButtonProps: IInputTracker<IRadioButtonList> = {
            changeTelemetryCustom: { eventName: "ClmMakeChannelChangeType", actionType: "Undefined" },
            properties: {
                onChange: this.onVisaChannelSelect,
                groupName: "visa-channel-type",
                initialData: this.state.visaChannel,
                options: [
                    {
                        label: this.props.resources.onlineChannel,
                        value: "Online",
                        name: "Online",
                    },
                    {
                        label: this.props.resources.offlineChannel,
                        value: "Offline",
                        name: "Offline",
                    },
                ],
            },
        };

        return (
            <div>
                <span className={css.standardModalLabel}>{this.props.resources.visaChannelTitle}</span>
                <div>
                    <RadioButtonList
                        {...SetInputTrackerProps(radioButtonProps)}
                        ref="visaChannel-radioButtonList"
                    />
                    <ErrorContainer {...this.errors.visaChannelInvalidError.container} />
                </div>
            </div>
        );
    }

    private get renderPurchaseAmountValue() {
        const purchaseAmountTextbox: IInputTracker<ITextbox> = {
            ...this.props.purchaseAmountTextbox,
            focusTelemetryCustom: {
                eventName: "cshbck-clmMakeOrderAmountEdit",
                actionType: "Undefined",
            },
            properties: {
                ...this.props.purchaseAmountTextbox.properties,
                maxLength: 8,
                onChange: this.onChangeAmount,
                value: this.state.purchaseAmount,
                onBlur: this.onBlurAmount,
                preventPaste: true,
            },
        };

        return (
            <div>
                <label className={css.standardModalLabel} htmlFor={`${purchaseAmountTextbox.clientId}-input`}>
                    {this.props.purchaseAmountLabel}
                </label>
                <div>
                    <Textbox {...SetInputTrackerProps(purchaseAmountTextbox)} ref="amount-textbox" />
                    <ErrorContainer {...this.errors.amountInvalidError.container} />
                </div>
            </div>
        );
    }

    private get stateTransactionDate(): { year: number; month: number; day: number } | null {
        const date = this.state.date;

        if (date) {
            return {
                year: date.getFullYear(),
                month: date.getMonth() + 1,
                day: date?.getDate(),
            };
        }
        return {
            year: 0,
            month: 0,
            day: 0,
        };
    }

    public static readonly defaultProps: ICashbackMissingClaimForm = {
        ...(ComponentAjax.defaultProps as ICashbackMissingClaimForm),
        ajaxMethod: AjaxMethod.Post,
        ...CashbackMissingClaimFormResources,
        ...CalendarResources,
    };

    public errors: IErrors = {
        default: { ...this.defaultErrors().default, type: ErrorType.Validator },
        orderIdInvalidError: {
            name: "orderId-invalid",
            type: ErrorType.Validator,
            targetRef: "orderId-textbox",
            container: { ref: "orderId-invalid-container" },
            onValidate: () => {
                if (
                    (!this.props.fieldValues ||
                        this.props.fieldValues.cashbackClaimType !== CashbackClaimType.Visa) &&
                    isNullOrEmpty(this.state.orderId)
                ) {
                    return false;
                }

                return true;
            },
        },
        visaChannelInvalidError: {
            name: "visaChannel-invalid",
            type: ErrorType.Validator,
            targetRef: "visaChannel-radioButtonList",
            container: { ref: "visa-channel-invalid-container" },
            onValidate: () => {
                if (
                    this.props.fieldValues &&
                    this.props.fieldValues.cashbackClaimType === CashbackClaimType.Visa &&
                    this.state.visaChannel !== "Online" &&
                    this.state.visaChannel !== "Offline"
                ) {
                    return false;
                }

                return true;
            },
        },
        amountInvalidError: {
            name: "amount-invalid",
            type: ErrorType.Validator,
            targetRef: "amount-textbox",
            container: { ref: "amount-invalid-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.purchaseAmount)) {
                    return false;
                }

                return true;
            },
        },
        brandInvalidError: {
            name: "brand-invalid",
            type: ErrorType.Validator,
            targetRef: "brand-textbox",
            container: { ref: "brand-invalid-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.brandId)) {
                    return false;
                }

                return true;
            },
        },
        dateInvalidError: {
            name: "date-invalid",
            type: ErrorType.Validator,
            targetRef: "date-textbox",
            container: { ref: "date-invalid-container" },
            onValidate: () => {
                const { year, month, day } = this.stateTransactionDate;

                if (year > 0 && month > 0 && day > 0) {
                    return true;
                }

                return false;
            },
        },
        dateRangeInvalidError: {
            name: "date-range-invalid",
            type: ErrorType.Validator,
            targetRef: "date-textbox",
            container: { ref: "date-range-invalid-container" },
            onValidate: () => {
                const { year, month, day } = this.stateTransactionDate;

                const today = new Date();

                const lastMonth = new Date(
                    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() - 32, 0, 0, 0),
                );
                const inputDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));

                if (inputDate <= lastMonth) {
                    return false;
                }

                return true;
            },
        },
        dateRangeLessThan3Days: {
            name: "date-range-low",
            type: ErrorType.Validator,
            targetRef: "date-textbox",
            container: { ref: "date-range-low-container" },
            onValidate: () => !this.state.dateWarning,
        },
    };

    private onChangeTimeout: number;

    constructor(props: ICashbackMissingClaimForm) {
        super(props);

        const fields = this.props.fieldValues;

        if (fields) {
            this.state = {
                isLoading: false,
                purchaseAmount: fields.purchaseAmount || "",
                date: fields.dateFormat,
                brandName: fields.brandName || "",
                brandId: fields.brandId || "",
                imageUrl: fields.imageUrl || "",
                orderId: fields.orderId || "",
                purchaseAmountValue: fields.purchaseAmountValue || undefined,
                visaChannel: fields.visaChannelType,
            };
        } else {
            this.state = {
                isLoading: false,
                purchaseAmount: "",
                brandName: "",
                brandId: "",
                imageUrl: "",
                date: null,
                orderId: "",
                purchaseAmountValue: undefined,
            };
        }
    }

    public componentWillUnmount() {
        if (this.onChangeTimeout !== null) {
            clearTimeout(this.onChangeTimeout);
        }
    }

    public render() {
        const buttonsBar: IButtonsBar = this.props.buttonsBar && {
            ...this.props.buttonsBar,
            nextButton: {
                ...this.props.buttonsBar.nextButton,
                onClick: this.submitForm,
            },
            cancelButton: {
                ...this.props.buttonsBar.cancelButton,
                onClick: this.previousPage,
            },
        };

        return (
            <div className={`${css.cashbackMissingClaimFormComponent} ${css.standardModalForm}`}>
                <h1 className={css.standardModalTitle}>{this.props.title}</h1>
                <div className={css.standardModalFormBody}>
                    <div className={css.formRow}>
                        {this.renderTransactionDate}
                        {this.renderBrand}
                    </div>
                    <div className={css.formRow}>
                        {this.renderPurchaseAmountValue}
                        {this.props.fieldValues &&
                        this.props.fieldValues.cashbackClaimType === CashbackClaimType.Visa
                            ? this.renderVisaChannel
                            : this.renderOrderId}
                    </div>
                    {this.state.brandInfo && (
                        <div className={css.orderInfoWrapper}>
                            <div dangerouslySetInnerHTML={{ __html: this.state.brandInfo }} />
                        </div>
                    )}
                </div>
                <ButtonsBar {...buttonsBar} />
            </div>
        );
    }

    protected ajaxSuccess(response: IConfirmClaimDataResult): void {
        if (response) {
            if (response.errorMessage) {
                this.setState({ orderIdErrorMessage: response.errorMessage, isLoading: false });

                const refKey = this.errors.orderIdInvalidError.container.ref;

                if (!refKey || !this.refs[refKey]) {
                    return;
                }

                displayError(this as IErrorComponent, {
                    ...this.errors.orderIdInvalidError,
                    errorMessage: { message: response.errorMessage },
                });
            } else if (response.isValidated || response.communicationTemplate) {
                this.submit(response.communicationTemplate);
            }
        }
    }

    protected ajaxFail(): boolean {
        this.setState(() => ({ isLoading: false }));
        return true;
    }

    private submit = (communicationTemplate?: IUserCommunicationTemplate) => {
        const { year, month, day } = this.stateTransactionDate;

        let transactionDate: string | undefined;
        if (year && day && month) {
            transactionDate = formatDate(new Date(year, month - 1, day));
        } else {
            transactionDate = !year && !month && !day ? undefined : "";
        }

        const field: ICashbackClaimFields = {
            ...this.props.fieldValues,
            purchaseAmount: this.state.purchaseAmount,
            purchaseAmountValue: this.state.purchaseAmountValue,
            date: transactionDate,
            brandId: this.state.brandId,
            imageUrl: this.state.imageUrl,
            brandName: this.state.brandName,
            dateFormat: this.state.date,
            orderId: this.state.orderId,
            visaChannelType: this.state.visaChannel,
            communicationTemplate,
        };

        if (this.props.saveValues) {
            this.props.saveValues(field);
        }

        if (this.props.onSubmit) {
            this.props.onSubmit(communicationTemplate ? 1 : undefined);
        }
    };

    private onVisaChannelSelect = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ visaChannel: event.target.value as VisaChannelType });

    private submitForm = () => {
        if (this.props.fieldValues && this.props.fieldValues.cashbackClaimType === CashbackClaimType.Visa) {
            this.submit();
        }

        const { year, month, day } = this.stateTransactionDate;

        this.ajaxRequest(
            {
                brandId: this.state.brandId,
                orderId: this.state.orderId,
                orderDate: formatDate(new Date(year, month - 1, day)),
                cashbackClaimType: this.props.fieldValues && this.props.fieldValues.cashbackClaimType,
            },
            () => {
                this.setState({ isLoading: true });
            },
        );
    };

    private onChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (this.onChangeTimeout) {
            clearTimeout(this.onChangeTimeout);
        }

        const accountFormat: IAccountFormat = accountNumberFormat(event.target.value, this.props.language, 5);

        this.setState({
            purchaseAmount: accountFormat.numberFormat,
            purchaseAmountValue: accountFormat.amount,
        });
    };

    private onBlurAmount = () => {
        const accountFormat: IAccountFormat = accountNumberFormat(
            this.state.purchaseAmount,
            this.props.language,
            5,
        );

        this.setState({
            purchaseAmount: accountFormat.amountFormat,
            purchaseAmountValue: accountFormat.amount,
        });
    };

    private onChangeBrand = (event: React.ChangeEvent<HTMLInputElement>): boolean => {
        this.setState({ brandName: event.target.value, brandId: undefined });
        return true;
    };

    private onBlurSelected = (item: ISearchBoxResultItemExtension): void => {
        if (item && item.text && item.id) {
            this.setState({
                brandName: item.text,
                brandId: item.id,
                brandInfo: item.info,
                imageUrl: item.logo,
            });
        }
    };

    private onSelectBrand = (
        _event: React.SyntheticEvent<HTMLElement>,
        item: ISearchBoxResultItemExtension,
    ): boolean => {
        if (item && item.text) {
            this.setState({
                brandName: item.text,
                brandId: item.id,
                brandInfo: item.info,
                imageUrl: item.logo,
            });
        }

        return true;
    };

    private previousPage = () => {
        if (this.props.previousPage) {
            if (this.props.saveValues) {
                this.props.saveValues({});
            }

            this.props.previousPage();
        }
    };
}

const CashbackMissingClaimFormConnect = (props: ICashbackMissingClaimForm) => {
    const resources = languageSelector(
        CashbackMissingClaimFormResources,
        CashbackMissingClaimFormResourcesEn,
    );

    const resourcesCalendar = languageSelector(CalendarResources, CalendarResourcesEn);

    const { language } = browserStatics;

    return (
        <CashbackMissingClaimForm
            {...props}
            language={language}
            resources={resources}
            resourcesCalendar={resourcesCalendar}
        />
    );
};

const styles = stylex.create({
    calendarButton: {
        position: "absolute",
        left: "0.6em",
        top: "0.5em",
    },
});

export { CashbackMissingClaimFormConnect as CashbackMissingClaimForm };
